/**
 * Summit special-offer picker. Rendered only for `OfferType.Summit` offers.
 *
 * Keeps raw `<select>` elements (not Radix Select) because both pickers need an
 * empty "none" option, which Radix forbids. The summit + session are immutable
 * once persisted, so the selects disable when the offer already has a value.
 *
 * The platform fee line shows the offer's stored fee, which the server owns.
 *
 * The cross-cutting auto-set effects (forcing `type=Summit`, locking the
 * platform fee, reconciling the session against the chosen summit) live in the
 * orchestrator since they mutate form state outside this section.
 *
 * Aliases: summit section, summit picker, summit offer fields.
 */
import { useStore } from "@tanstack/react-form";
import { format } from "date-fns";

import { withForm } from "~/components/form/app-form";
import { useFieldError } from "~/components/form/fields/field-errors";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { FormField } from "~/components/ui/form-field";
import { formatDateOnly } from "~/lib/summit";
import type {
  CoachActiveSummitWorkshop,
  SummitForSpecialOffer,
} from "~/lib/summit";

import { offerFormOptions } from "../offer-form-options";

const SELECT_CLASS_NAME =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60";

export const SummitSection = withForm({
  ...offerFormOptions,
  props: {} as {
    availableSummits: SummitForSpecialOffer[];
    activeWorkshops: CoachActiveSummitWorkshop[];
    summitLocked: boolean;
    summitSessionLocked: boolean;
    /** The stored fee; null when the offer has none of its own. */
    platformFeeBps: number | null;
  },
  render: function Render({
    form,
    availableSummits,
    activeWorkshops,
    summitLocked,
    summitSessionLocked,
    platformFeeBps,
  }) {
    const selectedSummitId = useStore(form.store, (s) => s.values.summitId);
    const summitIdError = useFieldError("summitId");
    const summitSessionIdError = useFieldError("summitSessionId");
    const summitSessions = selectedSummitId
      ? activeWorkshops.filter(
          (workshop) => workshop.session.summitId === selectedSummitId,
        )
      : [];

    return (
      <Card>
        <CardHeader>
          <CardTitle>Summit Special</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4 rounded-md border border-dashed p-4">
            <form.Field name="summitId">
              {(field) => (
                <FormField label="Summit" error={summitIdError} required>
                  <select
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === "" ? null : e.target.value,
                      )
                    }
                    onBlur={field.handleBlur}
                    required
                    disabled={summitLocked}
                    className={SELECT_CLASS_NAME}
                  >
                    <option value="">Select a summit</option>
                    {availableSummits.map((summit) => (
                      <option key={summit.id} value={summit.id}>
                        {`${summit.title} (${formatDateOnly(
                          summit.startDate,
                          "MMM d",
                        )} – ${formatDateOnly(summit.endDate, "MMM d, yyyy")})`}
                      </option>
                    ))}
                  </select>
                </FormField>
              )}
            </form.Field>

            <form.Field name="summitSessionId">
              {(field) => (
                <FormField
                  label="Workshop session (optional)"
                  error={summitSessionIdError}
                  helpText={
                    summitSessionLocked
                      ? "Session is locked after saving. Contact support to update."
                      : undefined
                  }
                >
                  <select
                    value={field.state.value ?? ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === "" ? null : e.target.value,
                      )
                    }
                    onBlur={field.handleBlur}
                    disabled={summitSessionLocked}
                    className={SELECT_CLASS_NAME}
                  >
                    <option value="">No session selected</option>
                    {summitSessions.map((workshop) => (
                      <option
                        key={workshop.session.id}
                        value={workshop.session.id}
                      >
                        {`${format(workshop.session.startTimeUtc, "MMM d")}: ${
                          workshop.session.title
                        }`}
                      </option>
                    ))}
                  </select>
                </FormField>
              )}
            </form.Field>

            {platformFeeBps !== null && (
              <p className="text-sm text-muted-foreground">
                {`Platform fee is fixed at ${platformFeeBps / 100}% for summit specials.`}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  },
});
